import { DocumentProps, PDFViewer } from '@react-pdf/renderer';
import React from 'react';
import { PdfGenerator } from '~/components/PdfGenerator';
import { Card } from '~/components/ui/card';
import { useGlobalState } from '~/context/GlobalStateContext';

export const LivePreview: React.FC = () => {
  const { generalState } = useGlobalState();
  const [isReady, setIsReady] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Create a unique key that changes when the state changes
  // This forces a complete re-mount of PDFViewer
  const componentKey = React.useMemo(() => {
    return JSON.stringify({
      data: generalState.data,
      template: generalState.template,
      timestamp: Date.now(),
    });
  }, [generalState.data, generalState.template]);

  const Doc = React.useMemo(() => {
    try {
      setError(null);
      return React.createElement(PdfGenerator, {
        data: generalState.data,
        template: generalState.template,
      }) as React.ReactElement<DocumentProps>;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error creating PDF document:', err);
      return null;
    }
  }, [generalState.data, generalState.template]);

  // Handle component readiness with debounce
  React.useEffect(() => {
    setIsReady(false);
    setError(null);

    // Add small delay to prevent rapid updates
    const timer = setTimeout(() => {
      if (Doc) {
        setIsReady(true);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [Doc, componentKey]);

  // Error state
  if (error) {
    return (
      <div className='flex-1 flex flex-col items-center justify-center h-full'>
        <Card className='flex-1 flex items-center justify-center p-0 w-full overflow-hidden'>
          <div className='p-4 text-center'>
            <div className='text-red-600 mb-2'>PDF Generation Error</div>
            <div className='text-sm text-gray-600'>{error}</div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className='flex-1 flex flex-col items-center justify-center h-full'>
      <Card className='flex-1 flex items-center justify-center p-0 w-full overflow-hidden'>
        {isReady && Doc ? (
          <PDFViewer
            key={componentKey} // Force re-mount on changes
            className='flex-1 overflow-hidden w-full'
          >
            {Doc}
          </PDFViewer>
        ) : (
          <div className='p-4 flex items-center gap-2'>
            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500'></div>
            <span>Loading PDF...</span>
          </div>
        )}
      </Card>
    </div>
  );
};
